/**
 * Avatar-to-3D Service
 * 
 * Analyzes avatar explanation scripts to detect 3D objects
 * and creates avatar_to_3d_assets for approval and generation
 */

import api from '../config/axios';
import { db } from '../config/firebase';
import { collection, addDoc, query, where, getDocs, doc, updateDoc, serverTimestamp, getDoc, deleteDoc } from 'firebase/firestore';
import { aiDetectionService } from './aiDetectionService';
import type { AIDetectionResult } from './aiDetectionService';
import { matchPromptsAgainstLibrary, type LibraryMatch } from './assetLibraryMatchService';
import { linkAssetToTopic } from '../lib/firestore/updateHelpers';

export interface AvatarTo3dAsset {
  id: string;
  chapter_id: string;
  topic_id: string;
  language: string;
  prompt: string; // The detected 3D object prompt
  source_script: string; // The original avatar explanation script
  source_script_type: 'explanation' | 'intro' | 'outro';
  approval_status?: boolean;
  status?: 'pending' | 'approved' | 'generating' | 'ready' | 'failed';
  generation_progress?: number;
  generation_message?: string;
  generation_error?: string;
  meshy_asset_id?: string;
  model_urls?: {
    glb?: string;
    fbx?: string;
    usdz?: string;
  };
  glb_url?: string;
  fbx_url?: string;
  usdz_url?: string;
  thumbnail_url?: string;
  created_at?: any;
  updated_at?: any;
  created_by?: string;
  detected_at?: any;
  confidence?: number; // AI detection confidence
  /**
   * The library asset this row was satisfied from, instead of generating.
   * An exact subject match is linked on save; a near match is only ever a
   * suggestion the author accepts, so the two are kept apart.
   */
  reused_from_library?: boolean;
  suggested_asset_id?: string;
  library_match?: LibraryMatch | null;
}

export interface DetectionResult {
  success: boolean;
  assets: AvatarTo3dAsset[];
  error?: string;
}

export class AvatarTo3dService {
  /**
   * Tag each detected object with the library asset that already satisfies it.
   *
   * Done once for the whole batch rather than per row: the server loads the
   * library to answer, and asking four times would load it four times.
   */
  private async attachLibraryMatches(assets: AvatarTo3dAsset[]): Promise<AvatarTo3dAsset[]> {
    if (assets.length === 0) return assets;
    const matches = await matchPromptsAgainstLibrary(assets.map((a) => a.prompt));
    return assets.map((asset, i) => ({ ...asset, library_match: matches[i] ?? null }));
  }

  /**
   * Analyze avatar explanation script and detect 3D objects
   */
  async detect3DObjects(
    chapterId: string,
    topicId: string,
    language: string,
    explanationScript: string,
    provider: 'openai' | 'n8n' = 'openai'
  ): Promise<DetectionResult> {
    if (!explanationScript || !explanationScript.trim()) {
      return {
        success: false,
        assets: [],
        error: 'Explanation script is empty'
      };
    }

    try {
      console.log(`🔍 Analyzing avatar explanation script for 3D objects (provider: ${provider})...`);
      console.log('Script preview:', explanationScript.substring(0, 100) + '...');

      // Use extract-assets endpoint directly (more reliable)
      try {
        const extractResponse = await api.post<{ assets: string[]; success: boolean; error?: string; method?: string; warning?: string }>('/ai-detection/extract-assets', {
          prompt: explanationScript.trim(),
          provider
        });

        if (extractResponse.data.warning) {
          console.warn('⚠️', extractResponse.data.warning);
        }

        console.log('📦 Extract-assets response:', extractResponse.data);

        if (!extractResponse.data.success) {
          return {
            success: false,
            assets: [],
            error: extractResponse.data.error || 'Failed to extract 3D objects'
          };
        }

        const extractedAssets = extractResponse.data.assets || [];

        if (extractedAssets.length === 0) {
          console.log('ℹ️ No 3D objects detected in avatar script');
          return {
            success: true,
            assets: []
          };
        }

        console.log(`✅ Detected ${extractedAssets.length} 3D object(s):`, extractedAssets);

        // Create avatar_to_3d_assets
        const assets: AvatarTo3dAsset[] = extractedAssets.map((prompt) => ({
          id: '', // Will be set when saved
          chapter_id: chapterId,
          topic_id: topicId,
          language,
          prompt: prompt.trim(),
          source_script: explanationScript,
          source_script_type: 'explanation',
          approval_status: false,
          status: 'pending',
          confidence: 0.8, // Default confidence
        }));

        return {
          success: true,
          assets: await this.attachLibraryMatches(assets)
        };
      } catch (extractError: any) {
        console.error('❌ Extract-assets endpoint failed:', extractError);
        
        // Fallback: Try detectPromptType
        console.log('🔄 Trying fallback detection method...');
        const detectionResult = await aiDetectionService.detectPromptType(explanationScript, provider);

        if (detectionResult.success && detectionResult.data) {
          const aiData = detectionResult.data;
          const meshAssets = aiData.meshAssets || [];

          if (meshAssets.length > 0) {
            console.log(`✅ Fallback detected ${meshAssets.length} 3D object(s):`, meshAssets);
            
            const assets: AvatarTo3dAsset[] = meshAssets.map((prompt) => ({
              id: '',
              chapter_id: chapterId,
              topic_id: topicId,
              language,
              prompt: prompt.trim(),
              source_script: explanationScript,
              source_script_type: 'explanation',
              approval_status: false,
              status: 'pending',
              confidence: aiData.confidence || 0.7,
            }));

            return {
              success: true,
              assets: await this.attachLibraryMatches(assets)
            };
          }
        }

        return {
          success: false,
          assets: [],
          error: extractError.response?.data?.error || 
                 extractError.message || 
                 detectionResult.error || 
                 'Failed to detect 3D objects'
        };
      }
    } catch (error) {
      console.error('❌ Error detecting 3D objects from avatar script:', error);
      return {
        success: false,
        assets: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Save detected assets to Firestore
   */
  async saveDetectedAssets(assets: Omit<AvatarTo3dAsset, 'id'>[]): Promise<string[]> {
    const assetIds: string[] = [];

    for (const asset of assets) {
      try {
        // Check if asset already exists (same prompt, chapter, topic, language)
        const existingQuery = query(
          collection(db, 'avatar_to_3d_assets'),
          where('chapter_id', '==', asset.chapter_id),
          where('topic_id', '==', asset.topic_id),
          where('language', '==', asset.language),
          where('prompt', '==', asset.prompt)
        );

        const existingDocs = await getDocs(existingQuery);
        
        if (!existingDocs.empty) {
          console.log(`ℹ️ Asset already exists: ${asset.prompt.substring(0, 50)}...`);
          assetIds.push(existingDocs.docs[0].id);
          continue;
        }

        // Create new asset - exclude id and any undefined to avoid Firestore invalid-argument
        const { id: _id, ...assetData } = asset as AvatarTo3dAsset;
        const cleanData: Record<string, unknown> = {
          chapter_id: assetData.chapter_id,
          topic_id: assetData.topic_id,
          language: assetData.language,
          prompt: assetData.prompt,
          source_script: assetData.source_script || '',
          source_script_type: assetData.source_script_type || 'explanation',
          approval_status: false,
          status: 'pending',
          confidence: assetData.confidence ?? 0.8,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          detected_at: serverTimestamp(),
        };

        /*
          An object the library already holds is attached here rather than
          generated. The row is written as finished, because that is what it is:
          every consumer keys off `status` and `meshy_asset_id`, and a reused
          asset is as ready as a generated one. A near match is not acted on —
          it is recorded as a suggestion, since reuse picked by similarity alone
          can put the wrong model in a lesson.
        */
        const match = assetData.library_match;
        if (match?.kind === 'exact') {
          const linked = await linkAssetToTopic(assetData.chapter_id, assetData.topic_id, match.assetId);
          if (linked) {
            cleanData.status = 'ready';
            cleanData.approval_status = true;
            cleanData.meshy_asset_id = match.assetId;
            cleanData.reused_from_library = true;
            cleanData.generation_message = 'Reused from the 3D asset library';
          }
        } else if (match?.kind === 'fuzzy') {
          // Name and thumbnail are denormalised so the suggestion can be shown
          // without a second read per row just to render a card.
          cleanData.suggested_asset_id = match.assetId;
          cleanData.suggested_asset_name = match.name;
          cleanData.suggested_asset_thumbnail = match.thumbnailUrl || '';
        }

        const docRef = await addDoc(collection(db, 'avatar_to_3d_assets'), cleanData);

        assetIds.push(docRef.id);
        console.log(`✅ Saved detected asset: ${docRef.id} - ${asset.prompt.substring(0, 50)}...`);
      } catch (error) {
        console.error('❌ Error saving detected asset:', error);
      }
    }

    return assetIds;
  }

  /**
   * Get all avatar_to_3d_assets for a topic
   */
  async getAssetsForTopic(
    chapterId: string,
    topicId: string,
    language?: string
  ): Promise<AvatarTo3dAsset[]> {
    const assetsRef = collection(db, 'avatar_to_3d_assets');
    const constraints: any[] = [
      where('chapter_id', '==', chapterId),
      where('topic_id', '==', topicId)
    ];

    if (language) {
      constraints.push(where('language', '==', language));
    }

    const q = query(assetsRef, ...constraints);
    const snapshot = await getDocs(q);

    const assets: AvatarTo3dAsset[] = [];
    snapshot.forEach((docSnap) => {
      assets.push({
        ...docSnap.data(),
        /*
          The document id wins, always — hence last, after the spread.

          Twelve documents in this collection carry their own `id` field set to
          the empty string, written before saveDetectedAssets started stripping
          it. With the spread last, that "" overwrote the real document id, so
          every AI-detected object in an affected topic arrived with id: "".
          Approving one then called doc(db, 'avatar_to_3d_assets', '') and threw
          "Document references must have an even number of segments", which is
          not a clue anybody could act on.
        */
        id: docSnap.id,
      } as AvatarTo3dAsset);
    });

    return assets;
  }

  /**
   * Update asset approval status
   */
  async updateApprovalStatus(
    assetId: string,
    approved: boolean,
    userId: string
  ): Promise<void> {
    const assetRef = doc(db, 'avatar_to_3d_assets', assetId);
    await updateDoc(assetRef, {
      approval_status: approved,
      approved_at: approved ? serverTimestamp() : null,
      approved_by: approved ? userId : null,
      updated_at: serverTimestamp(),
      ...(approved && !(await getDoc(assetRef)).data()?.meshy_asset_id ? { status: 'generating' } : {}),
    });
  }

  /**
   * Edit a detected asset before it is generated.
   *
   * The detector's prompt is a starting point, not a verdict — an admin who
   * knows the lesson can usually write a better one, and that choice has to be
   * made before generation rather than after.
   */
  async updateAsset(assetId: string, updates: { prompt?: string }): Promise<void> {
    const payload: Record<string, ReturnType<typeof serverTimestamp> | string> = {
      updated_at: serverTimestamp(),
    };
    if (typeof updates.prompt === 'string') payload.prompt = updates.prompt;
    await updateDoc(doc(db, 'avatar_to_3d_assets', assetId), payload);
  }

  /**
   * Manually create a 3D asset entry
   */
  async createManualAsset(
    chapterId: string,
    topicId: string,
    language: string,
    prompt: string,
    sourceScript?: string,
    userId?: string
  ): Promise<string> {
    // Check if asset already exists
    const existingQuery = query(
      collection(db, 'avatar_to_3d_assets'),
      where('chapter_id', '==', chapterId),
      where('topic_id', '==', topicId),
      where('language', '==', language),
      where('prompt', '==', prompt.trim())
    );

    const existingDocs = await getDocs(existingQuery);
    
    if (!existingDocs.empty) {
      console.log(`ℹ️ Asset already exists: ${prompt.substring(0, 50)}...`);
      return existingDocs.docs[0].id;
    }

    // Create new asset with auto-approval for manual entries
    const docRef = await addDoc(collection(db, 'avatar_to_3d_assets'), {
      chapter_id: chapterId,
      topic_id: topicId,
      language,
      prompt: prompt.trim(),
      source_script: sourceScript || '',
      source_script_type: 'explanation',
      approval_status: true, // Auto-approve manual entries
      status: 'generating', // Start generating immediately
      confidence: 1.0, // Manual entry has full confidence
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
      detected_at: serverTimestamp(),
      approved_at: serverTimestamp(),
      approved_by: userId || null,
      created_by: userId || null,
    });

    console.log(`✅ Created manual asset (auto-approved): ${docRef.id} - ${prompt.substring(0, 50)}...`);
    return docRef.id;
  }

  /**
   * Delete an avatar_to_3d_asset
   */
  async deleteAsset(assetId: string): Promise<void> {
    const assetRef = doc(db, 'avatar_to_3d_assets', assetId);
    await deleteDoc(assetRef);
    console.log(`✅ Deleted avatar_to_3d_asset: ${assetId}`);
  }
}

export const avatarTo3dService = new AvatarTo3dService();
